/** Presentation only: resolved purchase state remains owned by each buy-box controller. */
export function syncPdpIdentity(buyBox, { price, available, subscribed, quantity = 1 }) {
  const grid = buyBox.closest('product-component');
  const identity = grid?.querySelector('[data-pogi-identity-purchase]');
  if (!identity) return;
  const priceNode = identity.querySelector('[data-pogi-identity-price]');
  const termsNode = identity.querySelector('[data-pogi-identity-terms]');
  const frequency = subscribed
    ? buyBox.querySelector('[data-pogi-interval-select]')?.selectedOptions?.[0]?.textContent.trim()
    : '';
  const mode = subscribed ? identity.dataset.subscribeLabel : identity.dataset.onetimeLabel;
  priceNode.textContent = available && price ? price : identity.dataset.unavailableLabel;
  termsNode.textContent = available
    ? [mode, frequency, quantity > 1 ? `× ${quantity}` : ''].filter(Boolean).join(' · ')
    : '';
}

/** Reuse merchant-authored supporting content without moving its source out of its section.
 * Copies contain no forms, IDs or custom controls. Original sections stay available to
 * no-JS/fallback pages and after a main-section replacement.
 */
class PogiPdpSupport extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const update = () => this.renderSupportingContent();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', update, { once: true, signal: this.events.signal });
    } else update();
    document.addEventListener('shopify:section:load', update, { signal: this.events.signal });
  }

  renderSupportingContent() {
    const main = this.closest('main');
    if (!main || !this.closest('.pogi-buy-box')) return;
    const sources = [
      ['.pogi-details__cards', '[data-pogi-specs-content]', '[data-pogi-nearby-specs]'],
      ['.pogi-faq__list', '[data-pogi-faq-content]', '[data-pogi-nearby-faq]'],
    ];
    for (const [sourceSelector, targetSelector, disclosureSelector] of sources) {
      const source = [...main.querySelectorAll(sourceSelector)].find((node) => !node.closest('pogi-pdp-support'));
      const target = this.querySelector(targetSelector);
      const disclosure = this.querySelector(disclosureSelector);
      if (!source || !target || !disclosure || this.contains(source)) continue;
      const copy = source.cloneNode(true);
      copy.removeAttribute('id');
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      copy.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      copy.querySelectorAll('script, form, input, button, select, textarea').forEach((node) => node.remove());
      copy.querySelectorAll('details[open]').forEach((node) => node.removeAttribute('open'));
      target.replaceChildren(copy);
      disclosure.hidden = false;
    }
  }

  disconnectedCallback() { this.events?.abort(); }
}

if (!customElements.get('pogi-pdp-support')) customElements.define('pogi-pdp-support', PogiPdpSupport);
